import { CONFIG } from '../config.js';
import { createNewsCard } from './newsCard.js';
import { renderSkeleton } from './skeleton.js';
import { renderStats, renderTopDomains } from './statsPanel.js';
import { toggleVisibility } from './dom.js';
import { getRemainingCount, hasNextPage } from '../utils/pagination.js';
import { selectVisibleStories } from '../store/selectors.js';

/**
 * Vista principale.
 *
 * È un osservatore dello store: riceve uno stato e porta la pagina a
 * corrispondergli. Non decide nulla e non chiama servizi — le interazioni
 * vengono inoltrate ai callback ricevuti in `handlers`.
 */

/**
 * @param {Record<string, HTMLElement|null>} elements riferimenti al DOM
 * @param {object} [handlers]
 * @param {(id:number) => void} [handlers.onToggleFavorite]
 * @returns {{ render: (state: object) => void }}
 */
export function createNewsListView(elements, handlers = {}) {
  const {
    list,
    skeleton,
    empty,
    error,
    loadMoreButton,
    loadMoreLabel,
    progress,
    listMeta,
    stats,
    domains,
    favoritesFilter,
    favoritesCount,
  } = elements;

  /** Firma dell'ultimo rendering: evita di ridisegnare una lista identica. */
  let lastSignature = '';

  /**
   * Ridisegna solo se l'elenco visibile è cambiato; se sono cambiati unicamente
   * i preferiti, aggiorna le card interessate senza rimontare tutta la lista
   * (così l'animazione di ingresso non riparte a ogni click).
   *
   * @param {Array<object>} visible
   * @param {Set<number>} favorites
   */
  function renderList(visible, favorites) {
    if (!list) return;

    const signature = visible.map((story) => story.id).join(',');
    const now = new Date();

    if (signature === lastSignature) {
      syncFavoriteState(favorites);
      return;
    }

    lastSignature = signature;

    const fragment = document.createDocumentFragment();
    visible.forEach((story, i) => {
      fragment.append(
        createNewsCard(story, {
          index: i + 1,
          isFavorite: favorites.has(story.id),
          now,
          onToggleFavorite: handlers.onToggleFavorite,
        }),
      );
    });

    list.replaceChildren(fragment);
  }

  /** Allinea l'aspetto delle card allo stato dei preferiti. */
  function syncFavoriteState(favorites) {
    if (!list) return;

    for (const card of list.children) {
      const id = Number(card.dataset.storyId);
      const isFav = favorites.has(id);
      card.classList.toggle('news-card--favorite', isFav);

      const button = card.querySelector('[data-favorite-id]');
      if (!button) continue;
      button.setAttribute('aria-pressed', String(isFav));
      button.textContent = isFav ? '★' : '☆';
      const label = isFav ? 'Rimuovi dai preferiti' : 'Salva nei preferiti';
      button.setAttribute('aria-label', label);
      button.title = label;
    }
  }

  /** Testo dello stato vuoto, diverso a seconda del perché è vuoto. */
  function renderEmptyState(state, visibleCount) {
    const hasStories = state.stories.length > 0;
    const isFiltering = Boolean(state.search) || state.showFavoritesOnly;
    const show = !state.isLoading && !state.error && visibleCount === 0;

    toggleVisibility(empty, show);
    if (!show || !empty) return;

    const title = empty.querySelector('[data-empty-title]');
    const text = empty.querySelector('[data-empty-text]');

    if (hasStories && isFiltering) {
      if (title) title.textContent = 'Nessuna news corrisponde ai filtri';
      if (text) {
        text.textContent = state.showFavoritesOnly
          ? 'Non hai ancora salvato news tra i preferiti, oppure la ricerca è troppo stretta.'
          : 'Prova con un altro termine di ricerca, oppure carica altre news.';
      }
    } else {
      if (title) title.textContent = 'Nessuna news da mostrare';
      if (text) text.textContent = 'Premi "Aggiorna" per riprovare a contattare Hacker News.';
    }
  }

  /** Aggiorna pulsante "Load more", conteggi e messaggi di avanzamento. */
  function renderProgress(state, visibleCount) {
    const totalIds = state.storyIds.length;
    const loaded = state.stories.length;
    const remaining = getRemainingCount(totalIds, state.loadedPages * CONFIG.PAGE_SIZE);
    const more = hasNextPage(state.storyIds, state.loadedPages, CONFIG.PAGE_SIZE);

    if (loadMoreButton) {
      loadMoreButton.disabled = !more || state.isLoading || state.isLoadingMore;
      loadMoreButton.classList.toggle('is-loading', state.isLoadingMore);
      toggleVisibility(loadMoreButton, more);
    }

    if (loadMoreLabel) {
      loadMoreLabel.textContent = `Carica altre ${Math.min(CONFIG.PAGE_SIZE, remaining)} news`;
    }

    if (progress) {
      if (state.isLoading) progress.textContent = 'Contatto Hacker News…';
      else if (!totalIds) progress.textContent = '';
      else if (more) progress.textContent = `${loaded} news caricate · ${remaining} ancora disponibili`;
      else progress.textContent = `Sei arrivato in fondo: tutte le ${loaded} news sono state caricate.`;
    }

    if (listMeta) {
      listMeta.textContent = totalIds
        ? `${visibleCount} di ${loaded} news mostrate · ${totalIds} ID disponibili`
        : '';
    }
  }

  /**
   * Punto di ingresso della vista: chiamato a ogni cambio di stato.
   * @param {object} state
   */
  function render(state) {
    const visible = selectVisibleStories(state);
    const favorites = new Set(state.favorites);

    // Caricamento
    const busy = state.isLoading || state.isLoadingMore;
    toggleVisibility(skeleton, busy);
    if (busy) renderSkeleton(skeleton, CONFIG.PAGE_SIZE);
    list?.setAttribute('aria-busy', String(busy));

    // Errore
    toggleVisibility(error, Boolean(state.error));
    if (state.error && error) {
      const message = error.querySelector('[data-error-message]');
      if (message) message.textContent = state.error;
    }

    // Contenuto
    renderList(visible, favorites);
    renderEmptyState(state, visible.length);
    renderProgress(state, visible.length);

    // Pannelli derivati
    renderStats(stats, state.stories);
    renderTopDomains(domains, state.stories);

    // Toolbar
    if (favoritesCount) favoritesCount.textContent = String(state.favorites.length);
    if (favoritesFilter) {
      favoritesFilter.setAttribute('aria-pressed', String(state.showFavoritesOnly));
    }
  }

  return { render };
}
